import { useEffect, useState } from "react";
import { parseTimeInput } from "@/lib/clock";

interface Props {
  id: string;
  value: string | undefined;
  onChange: (next: string | undefined) => void;
  disabled?: boolean;
  className?: string;
  "data-testid"?: string;
  /**
   * Reject an otherwise-parseable value. Returning false leaves the draft
   * text alone and simply does not commit, so a rejected value never
   * interferes with typing — "0" parsing to 00:00 on the way to "0720" is
   * silently not stored rather than blocking the keystroke.
   */
  accept?: (parsed: string) => boolean;
}

/**
 * A 24-hour time field. Deliberately NOT `<input type="time">`: that control
 * takes its format from the browser's UI language, so an English-UI browser
 * shows AM/PM regardless of the page's `lang`, the element's `lang`, or
 * `LC_TIME` (docs/adr/0008). Hand-building it is the only way to guarantee
 * 24-hour on every machine and on the installed PWA.
 *
 * Typing is never interfered with — the draft is committed the moment it
 * parses, and normalised to "HH:MM" on blur. Text that never parses is
 * discarded on blur, leaving the last good value.
 */
export function TimeField({
  id,
  value,
  onChange,
  disabled = false,
  className = "clock-input",
  "data-testid": testId,
  accept,
}: Props) {
  const [draft, setDraft] = useState(value ?? "");
  const [focused, setFocused] = useState(false);

  // Follow external changes (a different day, a seeded week) while idle,
  // but never yank the text out from under someone mid-edit.
  useEffect(() => {
    if (!focused) setDraft(value ?? "");
  }, [value, focused]);

  const commit = (parsed: string): boolean => {
    if (accept && !accept(parsed)) return false;
    onChange(parsed);
    return true;
  };

  const handleChange = (next: string) => {
    setDraft(next);
    if (next.trim() === "") {
      onChange(undefined);
      return;
    }
    const parsed = parseTimeInput(next);
    if (parsed !== null) commit(parsed);
  };

  const handleBlur = () => {
    setFocused(false);
    if (draft.trim() === "") {
      setDraft("");
      onChange(undefined);
      return;
    }
    const parsed = parseTimeInput(draft);
    if (parsed !== null && commit(parsed)) {
      setDraft(parsed);
    } else {
      setDraft(value ?? "");
    }
  };

  return (
    <input
      id={id}
      type="text"
      className={className}
      inputMode="numeric"
      autoComplete="off"
      maxLength={5}
      placeholder="--:--"
      value={draft}
      disabled={disabled}
      onFocus={() => setFocused(true)}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={handleBlur}
      data-testid={testId}
    />
  );
}
